import { TFunction } from "@impero-com/common/utils/i18n";
import { TST_COLUMN_SIZE } from "@impero-com/ui";
import { createColumnHelper } from "@tanstack/react-table";

import {
    AdminEditViewAccessRights,
    EntityListLinkedResources,
} from "api/generated/model";
import { AccessRightTags } from "components/accessRightTags";
import { toUsersByAccessRights } from "components/accessRightTags/utils";
import { TagAndCategoryList } from "components/tag/tagAndCategory";
import { extractTagsAndCategoriesFromIds } from "org/tags/tagsTSTUtils";
import { WebRoute } from "routes";

const COLUMN_ID_NAME = "name";
const COLUMN_ID_TAGS = "tags";
const COLUMN_ID_ACCESS_RIGHTS = "controlAccessRights";

export interface EntityTableRow {
    entityId: number;
    name: string;
    tagIds: string[];
    controlAccessRights: AdminEditViewAccessRights;
}

export function buildSharedColumns(
    t: TFunction,
    linkedResources: EntityListLinkedResources,
    setAddEntitiesOpen?: (open: boolean) => void
) {
    const columnHelper = createColumnHelper<EntityTableRow>();
    const { users } = linkedResources;
    const handleDrawerClose = () => {
        if (setAddEntitiesOpen) {
            setAddEntitiesOpen(false);
        }
    };
    return [
        columnHelper.accessor((row) => row.name, {
            id: COLUMN_ID_NAME,
            header: t("Entity_Label_Name"),
            size: TST_COLUMN_SIZE.XXL,
            cell: (info) => {
                const entityId = info.row.original.entityId;
                return (
                    <a
                        onClick={handleDrawerClose}
                        rel="noopener noreferrer"
                        target="_blank"
                        href={buildControlEntityUrl(entityId)}
                    >
                        {info.getValue()}
                    </a>
                );
            },
        }),
        columnHelper.accessor((row) => row.tagIds, {
            id: COLUMN_ID_TAGS,
            header: t("Entity_Label_Tags"),
            size: TST_COLUMN_SIZE.XL,
            cell: (info) => {
                const { tags, tagCategories } = extractTagsAndCategoriesFromIds(
                    info.row.original.tagIds,
                    linkedResources.tags,
                    linkedResources.tagCategories
                );
                return (
                    <TagAndCategoryList
                        tags={tags}
                        tagCategories={tagCategories}
                    />
                );
            },
        }),

        columnHelper.accessor((row) => row.controlAccessRights, {
            id: COLUMN_ID_ACCESS_RIGHTS,
            header: t("Entity_Column_Header_ControlAccessRights"),
            size: TST_COLUMN_SIZE.XL,
            cell: (info) => {
                return (
                    <AccessRightTags
                        usersByAccessRight={toUsersByAccessRights(
                            info.getValue(),
                            users
                        )}
                    />
                );
            },
        }),
    ];
}

export function buildControlEntityUrl(entityId: number) {
    return `${WebRoute.ENTITIES}/${entityId}` as const;
}
